import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Title3,
  Body1,
  Spinner,
  Button,
  makeStyles,
  tokens,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Tooltip,
  Dropdown,
  Option,
  Input,
  Checkbox,
  ProgressBar,
} from '@fluentui/react-components';
import {
  ArrowSyncRegular,
  DeleteRegular,
  DocumentTextRegular,
  SearchRegular,
  DismissRegular,
  DeleteDismissRegular,
  // Ícones para tipos de operação
  PersonAdd20Regular,
  PersonDelete20Regular,
  PersonEdit20Regular,
  PeopleTeam20Regular,
  Building20Regular,
  DocumentSync20Regular,
  // Ícones para erros
  ErrorCircle20Regular,
  Warning20Regular,
  // Ícone para tentativas
  ArrowRepeatAll20Regular,
} from '@fluentui/react-icons';
import { dlqService } from '../../services/dlqService';
import { snapshotService } from '../../services/snapshotService';
import type { DlqMessageDetails, SnapshotMessage, DlqOperationResponse } from '../../types';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  tableCard: {
    padding: '20px',
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
  successText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  codeBlock: {
    backgroundColor: tokens.colorNeutralBackground3,
    padding: '12px',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
    overflowX: 'auto',
    maxHeight: '400px',
  },
  filterBar: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
    flexWrap: 'wrap',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '8px',
  },
  filterDropdown: {
    minWidth: '180px',
  },
  searchInput: {
    minWidth: '280px',
    flexGrow: 1,
  },
  filterCount: {
    marginLeft: 'auto',
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  descriptionCell: {
    maxWidth: '250px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    cursor: 'help',
  },
  bulkActions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: '8px',
  },
  selectionInfo: {
    marginLeft: 'auto',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  correlationId: {
    fontFamily: 'monospace',
    fontSize: '11px',
    wordBreak: 'break-all',
  },
  compactCell: {
    fontSize: '12px',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '16px',
    gap: '12px',
    alignItems: 'center',
  },
  paginationInfo: {
    color: tokens.colorNeutralForeground3,
    fontSize: '13px',
  },
  // Estilos para células com ícones
  iconCell: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '22px',
    height: '22px',
    borderRadius: '4px',
    flexShrink: 0,
  },
  iconBlue: {
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
  },
  iconRed: {
    backgroundColor: tokens.colorPaletteRedBackground2,
    color: tokens.colorPaletteRedForeground1,
  },
  iconYellow: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground1,
  },
  iconGray: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
});

// Função para obter ícone baseado no tipo de operação
const getOperationIcon = (type: string) => {
  if (!type) return <DocumentSync20Regular />;
  const lowerType = type.toLowerCase();
  if (lowerType.includes('new') || lowerType.includes('create')) return <PersonAdd20Regular />;
  if (lowerType.includes('delete') || lowerType.includes('remove')) return <PersonDelete20Regular />;
  if (lowerType.includes('update') || lowerType.includes('edit')) return <PersonEdit20Regular />;
  if (lowerType.includes('section') || lowerType.includes('class') || lowerType.includes('roster') || lowerType.includes('enrollment')) return <PeopleTeam20Regular />;
  if (lowerType.includes('school')) return <Building20Regular />;
  return <DocumentSync20Regular />;
};

// Função para verificar se erro é recuperável
const isRecoverableError = (pattern: string): boolean => {
  if (!pattern) return false;
  const recoverable = ['Timeout', 'Rate Limited', 'Service Unavailable', 'Retry Limit Exceeded', 'Queue Disabled'];
  return recoverable.some(r => pattern.toLowerCase().includes(r.toLowerCase()));
};

const PAGE_SIZE = 100;

export const DlqList: React.FC = () => {
  const styles = useStyles();
  const [messages, setMessages] = useState<SnapshotMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMessage, setSelectedMessage] = useState<DlqMessageDetails | null>(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  // Estado de paginação (page-based)
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Estado dos filtros
  const [filters, setFilters] = useState({
    messageType: '' as string,
    errorPattern: '' as string,
    searchText: '' as string,
  });

  // Valores distintos vindos do backend
  const [distinctMessageTypes, setDistinctMessageTypes] = useState<string[]>([]);
  const [distinctErrorPatterns, setDistinctErrorPatterns] = useState<string[]>([]);

  // Estado para operação de purge assíncrona
  const [purgeOperation, setPurgeOperation] = useState<DlqOperationResponse | null>(null);
  const [isPurging, setIsPurging] = useState(false);

  // Carregar quando filtros (exceto searchText) mudam
  useEffect(() => {
    loadMessages(1);
  }, [filters.messageType, filters.errorPattern]);

  // Debounce para searchText (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMessages(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [filters.searchText]);

  // Mensagens já vêm filtradas do backend
  const filteredMessages = messages;

  // Estado de seleção
  const [selectedMessages, setSelectedMessages] = useState<Set<number>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const clearFilters = () => {
    setFilters({ messageType: '', errorPattern: '', searchText: '' });
  };

  const hasActiveFilters = filters.messageType || filters.errorPattern || filters.searchText;

  // Funções de seleção
  const toggleSelectAll = () => {
    if (selectedMessages.size === filteredMessages.length) {
      setSelectedMessages(new Set());
    } else {
      setSelectedMessages(new Set(filteredMessages.map(m => m.sequenceNumber)));
    }
  };

  const toggleSelect = (sequenceNumber: number) => {
    const newSelected = new Set(selectedMessages);
    if (newSelected.has(sequenceNumber)) {
      newSelected.delete(sequenceNumber);
    } else {
      newSelected.add(sequenceNumber);
    }
    setSelectedMessages(newSelected);
  };

  const isAllSelected = filteredMessages.length > 0 && selectedMessages.size === filteredMessages.length;
  const hasSelection = selectedMessages.size > 0;

  // Ações em lote
  const handleBulkReprocess = async () => {
    if (!confirm(`Tem certeza que deseja reprocessar ${selectedMessages.size} mensagem(ns)?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      await dlqService.reprocessBatch(Array.from(selectedMessages));
      alert(`${selectedMessages.size} mensagem(ns) reprocessada(s) com sucesso!`);
      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error bulk reprocessing:', err);
      alert(`Erro ao reprocessar: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Tem certeza que deseja DELETAR PERMANENTEMENTE ${selectedMessages.size} mensagem(ns)?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      for (const seqNum of selectedMessages) {
        await dlqService.deleteMessage(seqNum);
      }
      alert(`${selectedMessages.size} mensagem(ns) deletada(s) com sucesso!`);
      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error bulk deleting:', err);
      alert(`Erro ao deletar: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleReprocessAll = async () => {
    if (!confirm(`Tem certeza que deseja reprocessar TODAS as ${filteredMessages.length} mensagens?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      await dlqService.reprocessBatch(filteredMessages.map(m => m.sequenceNumber));
      alert(`${filteredMessages.length} mensagem(ns) reprocessada(s) com sucesso!`);
      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error reprocessing all:', err);
      alert(`Erro ao reprocessar: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!confirm(`Tem certeza que deseja DELETAR PERMANENTEMENTE TODAS as ${totalCount} mensagens da DLQ?\n\nEsta operação pode demorar vários minutos.`)) {
      return;
    }

    try {
      setIsPurging(true);
      setBulkLoading(true);

      // Inicia operação assíncrona
      const operation = await dlqService.startPurgeAsync();
      setPurgeOperation(operation);

      // Polling com callback para atualizar progresso
      const finalOp = await dlqService.pollOperation(
        operation.id,
        (op) => setPurgeOperation(op),
        2000 // 2 segundos entre polls
      );

      if (finalOp.status === 'Completed') {
        alert(`Operação concluída!\n\n${finalOp.processed} mensagens deletadas\n${finalOp.failed} falhas\nDuração: ${finalOp.durationFormatted}`);
      } else if (finalOp.status === 'Failed') {
        alert(`Erro na operação: ${finalOp.errorMessage}`);
      } else if (finalOp.status === 'Cancelled') {
        alert('Operação cancelada pelo usuário.');
      }

      setSelectedMessages(new Set());
      await loadMessages(1);
    } catch (err: any) {
      console.error('Error purging all:', err);
      alert(`Erro ao iniciar purge: ${err.message}`);
    } finally {
      setIsPurging(false);
      setPurgeOperation(null);
      setBulkLoading(false);
    }
  };

  const handleCancelPurge = async () => {
    if (purgeOperation && confirm('Tem certeza que deseja cancelar a operação?')) {
      try {
        await dlqService.cancelOperation(purgeOperation.id);
      } catch (err: any) {
        console.error('Error cancelling purge:', err);
      }
    }
  };

  const loadMessages = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      const data = await snapshotService.getDlqMessages(
        page,
        PAGE_SIZE,
        filters.messageType || undefined,
        filters.errorPattern || undefined,
        filters.searchText || undefined
      );

      setMessages(data.messages);
      setCurrentPage(data.page);
      setTotalPages(data.totalPages || 1);
      setTotalCount(data.totalCount);

      // Atualizar listas distintas do backend
      if (data.distinctMessageTypes) {
        setDistinctMessageTypes(data.distinctMessageTypes);
      }
      if (data.distinctErrorPatterns) {
        setDistinctErrorPatterns(data.distinctErrorPatterns);
      }

      // Limpar seleção ao mudar de página
      setSelectedMessages(new Set());
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar mensagens DLQ do snapshot');
      console.error('Error loading DLQ messages from snapshot:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.messageType, filters.errorPattern, filters.searchText]);

  const handleViewDetails = async (sequenceNumber: number) => {
    try {
      setDetailsLoading(true);
      const details = await dlqService.getMessageDetails(sequenceNumber);
      setSelectedMessage(details);
    } catch (err: any) {
      console.error('Error loading message details:', err);
      alert(`Erro ao carregar detalhes: ${err.message}`);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleReprocess = async (sequenceNumber: number) => {
    if (!confirm('Tem certeza que deseja reprocessar esta mensagem?')) {
      return;
    }

    try {
      setActionLoading(sequenceNumber);
      await dlqService.reprocessMessage(sequenceNumber);
      alert('Mensagem reprocessada com sucesso!');
      await loadMessages(1); // Reload list
    } catch (err: any) {
      console.error('Error reprocessing message:', err);
      alert(`Erro ao reprocessar: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (sequenceNumber: number) => {
    if (!confirm('Tem certeza que deseja DELETAR PERMANENTEMENTE esta mensagem?')) {
      return;
    }

    try {
      setActionLoading(sequenceNumber);
      await dlqService.deleteMessage(sequenceNumber);
      alert('Mensagem deletada com sucesso!');
      await loadMessages(1); // Reload list
    } catch (err: any) {
      console.error('Error deleting message:', err);
      alert(`Erro ao deletar: ${err.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <Spinner label="Carregando mensagens DLQ..." />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <Body1 className={styles.errorText}>Erro: {error}</Body1>
      </Card>
    );
  }

  return (
    <div className={styles.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title3>
          Dead Letter Queue ({totalCount} mensagens)
        </Title3>
        <Button appearance="primary" onClick={() => loadMessages(1)}>
          Atualizar
        </Button>
      </div>

      {/* Barra de Filtros */}
      {(messages.length > 0 || hasActiveFilters) && (
        <div className={styles.filterBar}>
          <Dropdown
            className={styles.filterDropdown}
            placeholder="Tipo de Mensagem"
            selectedOptions={filters.messageType ? [filters.messageType] : []}
            onOptionSelect={(_, data) => setFilters({ ...filters, messageType: (data.optionValue as string) || '' })}
          >
            <Option value="">Todos os Tipos</Option>
            {distinctMessageTypes.map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Dropdown>

          <Dropdown
            className={styles.filterDropdown}
            placeholder="Padrão de Erro"
            selectedOptions={filters.errorPattern ? [filters.errorPattern] : []}
            onOptionSelect={(_, data) => setFilters({ ...filters, errorPattern: (data.optionValue as string) || '' })}
          >
            <Option text="Todos os Padrões" value="">Todos os Padrões</Option>
            {distinctErrorPatterns.map(pattern => (
              <Option key={pattern} text={pattern} value={pattern}>{pattern}</Option>
            ))}
          </Dropdown>

          <Input
            className={styles.searchInput}
            placeholder="Buscar por External ID, Correlation ID..."
            contentBefore={<SearchRegular />}
            value={filters.searchText}
            onChange={(_, data) => setFilters({ ...filters, searchText: data.value || '' })}
          />

          {hasActiveFilters && (
            <Button
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={clearFilters}
            >
              Limpar
            </Button>
          )}

          <span className={styles.filterCount}>
            Página {currentPage} de {totalPages} ({totalCount} total)
          </span>
        </div>
      )}

      {/* Barra de Ações em Lote */}
      {messages.length > 0 && (
        <div className={styles.bulkActions}>
          <Button
            appearance="primary"
            icon={<ArrowSyncRegular />}
            disabled={!hasSelection || bulkLoading}
            onClick={handleBulkReprocess}
          >
            Reprocessar Selecionados ({selectedMessages.size})
          </Button>

          <Button
            appearance="secondary"
            icon={<DeleteRegular />}
            disabled={!hasSelection || bulkLoading}
            onClick={handleBulkDelete}
          >
            Apagar Selecionados ({selectedMessages.size})
          </Button>

          <div style={{ borderLeft: `1px solid ${tokens.colorNeutralStroke1}`, height: '24px', margin: '0 8px' }} />

          <Button
            appearance="subtle"
            icon={<ArrowSyncRegular />}
            disabled={bulkLoading || filteredMessages.length === 0}
            onClick={handleReprocessAll}
          >
            Reprocessar Tudo
          </Button>

          <Button
            appearance="subtle"
            icon={<DeleteDismissRegular />}
            disabled={bulkLoading || filteredMessages.length === 0}
            onClick={handleDeleteAll}
          >
            Apagar Tudo
          </Button>

          {bulkLoading && !isPurging && <Spinner size="tiny" label="Processando..." />}
        </div>
      )}

      {/* Barra de Progresso do Purge */}
      {isPurging && purgeOperation && (
        <Card style={{ padding: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Body1 style={{ fontWeight: 600 }}>
                Apagando DLQ... {purgeOperation.processed} de {purgeOperation.estimatedTotal}
              </Body1>
              <Button
                size="small"
                appearance="subtle"
                onClick={handleCancelPurge}
              >
                Cancelar
              </Button>
            </div>
            <ProgressBar value={purgeOperation.progressPercentage / 100} />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
              <span>{purgeOperation.progressPercentage}% completo</span>
              <span>Duração: {purgeOperation.durationFormatted}</span>
              {purgeOperation.failed > 0 && (
                <span style={{ color: tokens.colorPaletteRedForeground1 }}>
                  {purgeOperation.failed} falhas
                </span>
              )}
            </div>
          </div>
        </Card>
      )}

      {messages.length === 0 ? (
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <Body1>Nenhuma mensagem na DLQ</Body1>
        </Card>
      ) : filteredMessages.length === 0 ? (
        <Card style={{ padding: '40px', textAlign: 'center' }}>
          <Body1>Nenhuma mensagem encontrada com os filtros aplicados</Body1>
        </Card>
      ) : (
        <Card className={styles.tableCard}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell style={{ width: '40px' }}>
                  <Checkbox
                    checked={isAllSelected ? true : hasSelection ? 'mixed' : false}
                    onChange={toggleSelectAll}
                  />
                </TableHeaderCell>
                <TableHeaderCell style={{ width: '180px' }}>Correlation ID</TableHeaderCell>
                <TableHeaderCell style={{ width: '120px' }}>Tipo</TableHeaderCell>
                <TableHeaderCell style={{ width: '120px' }}>External ID</TableHeaderCell>
                <TableHeaderCell style={{ width: '100px' }}> Data/Hora</TableHeaderCell>
                <TableHeaderCell style={{ width: '100px' }}>Erro</TableHeaderCell>
                <TableHeaderCell style={{ width: '100px' }}>Tentativas</TableHeaderCell>
                <TableHeaderCell style={{ width: '100px' }}>Ações</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMessages.map((msg) => (
                <TableRow key={msg.sequenceNumber}>
                  <TableCell>
                    <Checkbox
                      checked={selectedMessages.has(msg.sequenceNumber)}
                      onChange={() => toggleSelect(msg.sequenceNumber)}
                    />
                  </TableCell>
                  <TableCell>
                    <span className={styles.correlationId}>
                      {msg.correlationId || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconBlue}`}>
                        {getOperationIcon(msg.messageType || '')}
                      </div>
                      <span>{msg.messageType || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={styles.compactCell} style={{ fontFamily: 'monospace' }}>
                      {msg.externalId || 'N/A'}
                    </span>
                  </TableCell>
                  <TableCell className={styles.compactCell}>{formatDate(msg.timestamp)}</TableCell>
                  <TableCell>
                    <Tooltip content={msg.deadLetterErrorDescription || msg.deadLetterReason || 'Sem descrição'} relationship="label">
                      <div className={styles.iconCell}>
                        <div className={`${styles.iconWrapper} ${isRecoverableError(msg.errorPattern || '') ? styles.iconYellow : styles.iconRed}`}>
                          {isRecoverableError(msg.errorPattern || '') ? <Warning20Regular /> : <ErrorCircle20Regular />}
                        </div>
                        <span>{msg.errorPattern || msg.deadLetterReason || 'Error'}</span>
                      </div>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <div className={styles.iconCell}>
                      <div className={`${styles.iconWrapper} ${styles.iconGray}`}>
                        <ArrowRepeatAll20Regular />
                      </div>
                      <span>{msg.retryCount}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={styles.actionsCell}>
                      <Dialog>
                        <DialogTrigger disableButtonEnhancement>
                          <Tooltip content="Ver detalhes" relationship="label">
                            <Button
                              size="small"
                              appearance="subtle"
                              icon={<DocumentTextRegular />}
                              onClick={() => handleViewDetails(msg.sequenceNumber)}
                              disabled={actionLoading === msg.sequenceNumber}
                            />
                          </Tooltip>
                        </DialogTrigger>
                        <DialogSurface style={{ maxWidth: '800px' }}>
                          <DialogBody>
                            <DialogTitle>Detalhes da Mensagem DLQ</DialogTitle>
                            <DialogContent>
                              {detailsLoading ? (
                                <Spinner label="Carregando detalhes..." />
                              ) : selectedMessage ? (
                                <div className={styles.dialogContent}>
                                  <div>
                                    <strong>Message ID:</strong> {selectedMessage.messageId}
                                  </div>
                                  <div>
                                    <strong>Correlation ID:</strong> {selectedMessage.correlationId}
                                  </div>
                                  <div>
                                    <strong>Tipo:</strong> {selectedMessage.messageType}
                                  </div>
                                  <div>
                                    <strong>External ID:</strong> {selectedMessage.externalId}
                                  </div>
                                  <div>
                                    <strong>Data DLQ:</strong> {formatDate(selectedMessage.deadLetterTime)}
                                  </div>
                                  <div>
                                    <strong>Enfileirado em:</strong> {formatDate(selectedMessage.enqueuedTimeUtc)}
                                  </div>
                                  <div>
                                    <strong>Motivo:</strong>{' '}
                                    <span className={styles.errorText}>{selectedMessage.deadLetterReason}</span>
                                  </div>
                                  <div>
                                    <strong>Descrição:</strong>{' '}
                                    <span className={styles.errorText}>
                                      {selectedMessage.deadLetterErrorDescription}
                                    </span>
                                  </div>
                                  <div>
                                    <strong>Tentativas:</strong> {selectedMessage.deliveryCount}
                                  </div>
                                  {Object.keys(selectedMessage.applicationProperties).length > 0 && (
                                    <div>
                                      <strong>Application Properties:</strong>
                                      <pre className={styles.codeBlock}>
                                        {JSON.stringify(selectedMessage.applicationProperties, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                  <div>
                                    <strong>Message Body:</strong>
                                    <pre className={styles.codeBlock}>{selectedMessage.messageBody}</pre>
                                  </div>
                                </div>
                              ) : (
                                <Body1>Nenhum detalhe carregado</Body1>
                              )}
                            </DialogContent>
                            <DialogActions>
                              <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Fechar</Button>
                              </DialogTrigger>
                            </DialogActions>
                          </DialogBody>
                        </DialogSurface>
                      </Dialog>

                      <Tooltip content="Reprocessar" relationship="label">
                        <Button
                          size="small"
                          appearance="primary"
                          icon={<ArrowSyncRegular />}
                          onClick={() => handleReprocess(msg.sequenceNumber)}
                          disabled={actionLoading === msg.sequenceNumber}
                        />
                      </Tooltip>

                      <Tooltip content="Deletar" relationship="label">
                        <Button
                          size="small"
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          onClick={() => handleDelete(msg.sequenceNumber)}
                          disabled={actionLoading === msg.sequenceNumber}
                        />
                      </Tooltip>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {/* Controles de Paginação */}
          {totalPages > 1 && (
            <div className={styles.loadMoreContainer}>
              <Button
                appearance="outline"
                disabled={currentPage === 1 || loading}
                onClick={() => loadMessages(currentPage - 1)}
              >
                Anterior
              </Button>

              <span className={styles.paginationInfo}>
                Página {currentPage} de {totalPages}
              </span>

              <Button
                appearance="outline"
                disabled={currentPage >= totalPages || loading}
                onClick={() => loadMessages(currentPage + 1)}
              >
                Próxima
              </Button>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default DlqList;
